import type { ReactNode } from 'react';
import { Botao } from './Botao';
import { juntarClasses } from './classes';
import { IconeAlerta } from './icones';

export interface PropsEstadoErro {
  titulo?: string;
  /** Uma frase em português sobre o que falhou, sem jargão de stack trace. */
  mensagem?: string;
  /**
   * Detalhe técnico (código do erro, id da requisição), recolhido.
   * Aparece só se o usuário abrir — mas existe, para ele conseguir relatar.
   * Nunca coloque aqui mensagem crua de banco ou de validação do backend: o
   * legado vazava nome de tabela e de coluna na tela (RN-513 e seguintes).
   */
  detalhe?: string;
  aoTentarNovamente?: () => void;
  /** Ação alternativa — "Voltar para a lista", "Falar com o suporte". */
  acaoSecundaria?: ReactNode;
  compacto?: boolean;
  className?: string;
}

/**
 * Estado de erro dentro da tela.
 *
 * Complementa o `LimiteDeErro`, que pega exceção de renderização: este aqui é
 * para a falha esperada — requisição que voltou 500, arquivo que não subiu — em
 * que a tela continua de pé e só um pedaço falhou.
 *
 * Três coisas sempre presentes, porque a ausência delas gerou chamado no legado:
 * o que falhou, a garantia de que nada foi perdido e um caminho de saída.
 */
export function EstadoErro({
  titulo = 'Não foi possível carregar',
  mensagem = 'Algo deu errado ao buscar estas informações. Seus dados não foram perdidos.',
  detalhe,
  aoTentarNovamente,
  acaoSecundaria,
  compacto = false,
  className,
}: PropsEstadoErro): JSX.Element {
  return (
    <div
      role="alert"
      className={juntarClasses(
        'flex flex-col items-center gap-3 rounded-painel border border-erro/30 bg-erro/5 text-center',
        compacto ? 'px-4 py-6' : 'px-6 py-12',
        className,
      )}
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-erro/10 text-erro">
        <IconeAlerta className="h-5 w-5" />
      </span>

      <h3 className="font-display text-base font-semibold tracking-tight text-conteudo-primario">
        {titulo}
      </h3>

      <p className="max-w-md text-sm leading-relaxed text-conteudo-secundario">{mensagem}</p>

      {(aoTentarNovamente !== undefined || acaoSecundaria !== undefined) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
          {aoTentarNovamente !== undefined && (
            <Botao variante="primario" tamanho="pequeno" onClick={aoTentarNovamente}>
              Tentar novamente
            </Botao>
          )}
          {acaoSecundaria}
        </div>
      )}

      {detalhe !== undefined && detalhe !== '' && (
        <details className="mt-2 w-full max-w-md text-left">
          <summary className="cursor-pointer rounded-card text-xs text-conteudo-secundario hover:text-conteudo-primario">
            Detalhes técnicos
          </summary>
          <p className="valor-fiscal mt-2 break-words rounded-card bg-superficie-vidro p-3 text-xs text-conteudo-secundario">
            {detalhe}
          </p>
        </details>
      )}
    </div>
  );
}
